import { Injectable, signal } from '@angular/core';
import { Observable, Subject, filter } from 'rxjs';

import { environment } from '../../../environments/environment';
import { RealtimeEvent, ServerMessage } from './realtime.types';

/**
 * One multiplexed WebSocket to the backend realtime endpoint (`/ws/v1/`).
 *
 * Callers `topic(name)` to get an Observable of events on that topic; the
 * service reference-counts subscriptions, (un)subscribes on the wire, and
 * transparently reconnects with backoff — resubscribing every active topic on
 * reopen. Auth needs no code here: the JWT `access_token` cookie is HttpOnly and
 * same-site, so the browser sends it on the WS handshake automatically.
 *
 * Realtime is an ENHANCEMENT, never load-bearing — consumers keep their polling
 * fallback running and use `connected()` only to hold off a few ticks. It says
 * the socket is OPEN, not that events are ARRIVING: the two came apart in
 * production (publisher and subscriber on different Redis databases). Never gate
 * a fallback on it indefinitely.
 * Built on the native `WebSocket` (no socket.io/rxjs-websocket dep — the bundle
 * budget is tight).
 */
@Injectable({ providedIn: 'root' })
export class RealtimeService {
  /** True while the socket is open and usable. Consumers gate their poll on this. */
  readonly connected = signal(false);

  private socket: WebSocket | null = null;
  private readonly events$ = new Subject<RealtimeEvent>();
  /** Active topic → subscriber count (reference-counted). */
  private readonly topics = new Map<string, number>();

  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly BACKOFF_BASE_MS = 1000;
  private static readonly BACKOFF_MAX_MS = 30_000;

  /**
   * Subscribe to a topic; emits every {@link RealtimeEvent} on it until the
   * caller unsubscribes. Lazily opens the socket on the first subscription and
   * sends the wire `subscribe` (once per topic, regardless of caller count).
   */
  topic(topic: string): Observable<RealtimeEvent> {
    return new Observable<RealtimeEvent>((subscriber) => {
      this.retain(topic);
      const sub = this.events$.pipe(filter((e) => e.topic === topic)).subscribe(subscriber);
      return () => {
        sub.unsubscribe();
        this.release(topic);
      };
    });
  }

  private retain(topic: string): void {
    const count = this.topics.get(topic) ?? 0;
    this.topics.set(topic, count + 1);
    if (count === 0) {
      this.ensureSocket();
      this.send({ action: 'subscribe', topic });
    }
  }

  private release(topic: string): void {
    const count = this.topics.get(topic) ?? 0;
    if (count <= 1) {
      this.topics.delete(topic);
      this.send({ action: 'unsubscribe', topic });
      if (this.topics.size === 0) {
        this.close();
      }
    } else {
      this.topics.set(topic, count - 1);
    }
  }

  private ensureSocket(): void {
    if (this.socket && this.socket.readyState <= WebSocket.OPEN) {
      return;
    }
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url());
    } catch {
      // Malformed URL / blocked — treat as a disconnect and back off.
      this.scheduleReconnect();
      return;
    }
    this.socket = ws;
    ws.onopen = () => {
      this.reconnectAttempts = 0;
      this.connected.set(true);
      // Resubscribe every active topic (covers a reconnect after drop).
      for (const topic of this.topics.keys()) {
        this.send({ action: 'subscribe', topic });
      }
    };
    ws.onmessage = (ev) => this.onMessage(ev);
    ws.onclose = () => {
      this.connected.set(false);
      this.socket = null;
      // Only try to reconnect while something still wants to listen.
      if (this.topics.size > 0) {
        this.scheduleReconnect();
      }
    };
    ws.onerror = () => {
      // onclose fires after onerror; let it drive the reconnect.
      ws.close();
    };
  }

  private onMessage(ev: MessageEvent): void {
    let msg: ServerMessage;
    try {
      msg = JSON.parse(ev.data as string) as ServerMessage;
    } catch {
      return;
    }
    if (msg.type === 'event') {
      this.events$.next({ topic: msg.topic, event: msg.event, payload: msg.payload });
    }
    // subscribed / unsubscribed / error acks are not surfaced to callers today;
    // a forbidden subscribe simply yields no events (the poll fallback covers it).
  }

  private send(message: { action: string; topic: string }): void {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    }
    // If not open yet, onopen resubscribes from `this.topics`, so a queued
    // subscribe isn't lost. An unsubscribe while closed is moot (no socket).
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) {
      return;
    }
    const delay = Math.min(
      RealtimeService.BACKOFF_BASE_MS * 2 ** this.reconnectAttempts,
      RealtimeService.BACKOFF_MAX_MS,
    );
    // Jitter avoids a thundering herd of reconnects after a server blip.
    const jittered = delay / 2 + Math.random() * (delay / 2);
    this.reconnectAttempts++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.topics.size > 0) {
        this.ensureSocket();
      }
    }, jittered);
  }

  private close(): void {
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempts = 0;
    if (this.socket) {
      this.socket.onclose = null; // don't trigger a reconnect on an intentional close
      this.socket.close();
      this.socket = null;
    }
    this.connected.set(false);
  }

  /** Derive the ws(s) URL from the configured API origin. */
  private url(): string {
    const base = environment.apiUrl.replace(/^http/, 'ws');
    return `${base}/ws/v1/`;
  }
}
